import type { LogEntry } from '../types';

/**
 * Dicionário de traduções de erros comuns EN → PT-BR
 */
const errorTranslations: Record<string, string> = {
  // Erros de não encontrado
  'Student not found': 'Estudante não encontrado no Entra ID',
  'Teacher not found': 'Professor não encontrado no Entra ID',
  'School not found': 'Escola não encontrada no Entra ID',
  'Class not found': 'Turma não encontrada no Entra ID',
  'User not found': 'Usuário não encontrado no Entra ID',
  'Resource not found': 'Recurso não encontrado no Entra ID',

  // Erros de existência
  'User already exists': 'Usuário já existe',
  'Student already exists': 'Estudante já existe',
  'Teacher already exists': 'Professor já existe',
  'Duplicate entry': 'Entrada duplicada',

  // Erros de validação
  'Invalid request': 'Requisição inválida',
  'Invalid data': 'Dados inválidos',
  'Invalid email': 'Email inválido',
  'Invalid format': 'Formato inválido',
  'Required field missing': 'Campo obrigatório ausente',
  'Validation failed': 'Validação falhou',

  // Erros de autorização
  'Unauthorized': 'Não autorizado',
  'Forbidden': 'Acesso negado',
  'Access denied': 'Acesso negado',
  'Insufficient permissions': 'Permissões insuficientes',

  // Erros de conexão/rede
  'Connection timeout': 'Tempo de conexão esgotado',
  'Network error': 'Erro de rede',
  'Service unavailable': 'Serviço indisponível',
  'Internal server error': 'Erro interno do servidor',

  // Erros do Graph API
  'Request_ResourceNotFound': 'Recurso não encontrado',
  'Request_BadRequest': 'Requisição inválida',
  'Request_Unauthorized': 'Não autorizado',
  'Request_Forbidden': 'Acesso negado',
  'ErrorAccessDenied': 'Acesso negado',
  'ErrorInvalidUser': 'Usuário inválido',

  // Erros de negócio
  'Enrollment limit exceeded': 'Limite de matrículas excedido',
  'Invalid grade': 'Série inválida',
  'Invalid role': 'Função inválida',
  'Class full': 'Turma lotada',
};

/**
 * Traduz uma mensagem de erro do inglês para português
 *
 * @param errorMessage Mensagem de erro em inglês
 * @returns Mensagem traduzida ou mensagem original se não encontrar tradução
 */
export function translateError(errorMessage: string): string {
  if (!errorMessage) {
    return errorMessage;
  }

  // Remove espaços extras
  const trimmed = errorMessage.trim();

  // Busca tradução exata (case-insensitive)
  const exactMatch = Object.entries(errorTranslations).find(
    ([key]) => key.toLowerCase() === trimmed.toLowerCase()
  );

  if (exactMatch) {
    return exactMatch[1];
  }

  // Busca por substring (case-insensitive)
  for (const [key, value] of Object.entries(errorTranslations)) {
    if (trimmed.toLowerCase().includes(key.toLowerCase())) {
      return value;
    }
  }

  // Se não encontrar tradução, retorna a mensagem original
  return errorMessage;
}

/**
 * Extrai a razão do erro de um evento de log
 *
 * @param event Evento de log
 * @returns Razão do erro traduzida ou null se não for evento de erro
 */
export function extractErrorReason(event: LogEntry): string | null {
  // Só processa eventos de erro
  if (event.level !== 'Error') {
    return null;
  }

  // Prioridade 1: Campo exception (se disponível)
  if (event.exception) {
    return translateError(event.exception);
  }

  // Prioridade 2: Extrair da mensagem
  // Padrões comuns:
  // - "Graph call failed. ... Reason: 'Student not found'"
  // - "Error: Student not found"
  // - "Failed: Student not found"

  const message = event.message || '';

  // Tenta extrair após "Reason:"
  const reasonMatch = message.match(/Reason:\s*["']?([^"',]+)["']?/i);
  if (reasonMatch && reasonMatch[1]) {
    return translateError(reasonMatch[1].trim());
  }

  // Tenta extrair após "Error:"
  const errorMatch = message.match(/Error:\s*["']?([^"',]+)["']?/i);
  if (errorMatch && errorMatch[1]) {
    return translateError(errorMatch[1].trim());
  }

  // Tenta extrair após "Failed:"
  const failedMatch = message.match(/Failed:\s*["']?([^"',]+)["']?/i);
  if (failedMatch && failedMatch[1]) {
    return translateError(failedMatch[1].trim());
  }

  // Se não conseguir extrair, retorna a mensagem completa traduzida
  return translateError(message);
}

/**
 * Determina o nome da entidade baseado no messageType
 */
function getEntityName(messageType: string | undefined): { singular: string; singularFem: string } {
  const type = messageType?.toLowerCase() || '';

  if (type.includes('student') || type.includes('enrollment')) {
    return { singular: 'Aluno', singularFem: 'Aluno' };
  }
  if (type.includes('teacher') || type.includes('teacherroster')) {
    return { singular: 'Professor', singularFem: 'Professor' };
  }
  if (type.includes('staff') || type.includes('staffroster')) {
    return { singular: 'Funcionário', singularFem: 'Funcionário' };
  }
  if (type.includes('school')) {
    return { singular: 'Escola', singularFem: 'Escola' };
  }
  if (type.includes('class')) {
    return { singular: 'Turma', singularFem: 'Turma' };
  }
  return { singular: 'Recurso', singularFem: 'Recurso' };
}

/**
 * Gera mensagem amigável baseada nos campos estruturados de erro do Graph API
 *
 * @param errorCode Código de erro do Graph API (ex: "Request_ResourceNotFound")
 * @param errorMessage Mensagem original do Graph API
 * @param messageType Tipo da mensagem (ex: "UpdateStudent", "NewTeacher") para contexto da entidade
 * @param resourceId Identificador do recurso (ex: email, externalId) para mostrar no tooltip
 * @returns Mensagem amigável em português
 */
export function getErrorFriendlyMessage(
  errorCode: string | undefined,
  errorMessage: string | undefined,
  messageType?: string,
  _resourceId?: string
): string {
  if (!errorCode && !errorMessage) {
    return 'Erro desconhecido';
  }

  const entity = getEntityName(messageType);

  // Duplicados (verifica mensagem do Graph)
  if (errorMessage?.includes('already exist')) {
    // Teams - verificar ANTES das outras entidades
    if (errorMessage.toLowerCase().includes('team already')) {
      return 'Team já existe';
    }
    if (messageType?.toLowerCase().includes('teacher') || errorMessage.includes('owners')) {
      return 'Professor já existe na turma';
    }
    if (messageType?.toLowerCase().includes('student') || errorMessage.includes('members')) {
      return 'Aluno já existe na turma';
    }
    if (messageType?.toLowerCase().includes('staff')) {
      return 'Funcionário já existe na turma';
    }
    return `${entity.singular} já existe`;
  }

  // Não encontrado (404)
  if (errorCode === 'Request_ResourceNotFound' || errorCode === 'ODataError') {
    return `${entity.singular} não encontrado no Entra ID`;
  }

  // Campos obrigatórios
  if (errorMessage?.includes('A value is required for property')) {
    const propMatch = errorMessage.match(/property '([^']+)'/);
    if (propMatch) {
      return `Campo obrigatório: ${propMatch[1]}`;
    }
    return 'Campo obrigatório faltando';
  }

  // Domínio inválido
  if (errorMessage?.includes('domain portion')) {
    return 'Domínio de email inválido';
  }

  // Sem permissão
  if (errorCode === 'AccessDenied' || errorCode === 'Request_Forbidden') {
    return 'Sem permissão';
  }

  // Token inválido
  if (errorCode === 'InvalidAuthenticationToken') {
    return 'Token inválido/expirado';
  }

  // Muitas requisições
  if (errorCode === 'TooManyRequests') {
    return 'Muitas requisições - aguarde';
  }

  // Erro interno
  if (errorCode === 'InternalServerError') {
    return 'Erro interno do Graph';
  }

  // Requisição inválida genérica
  if (errorCode === 'Request_BadRequest' || errorCode === 'BadRequest') {
    return 'Requisição inválida';
  }

  // Fallback: traduz a mensagem original
  return translateError(errorMessage || errorCode || 'Erro desconhecido');
}

/**
 * Formata o conteúdo do tooltip de erro com informações detalhadas
 */
export function formatErrorTooltip(
  errorStatusCode: number | undefined,
  errorCode: string | undefined,
  errorMessage: string | undefined,
  resourceId?: string
): { header: string; details: string } {
  const header = `${errorStatusCode || '???'}: ${errorCode || 'Erro'}`;

  let details = errorMessage || 'Sem detalhes disponíveis';

  // Adiciona o identificador do recurso se disponível
  if (resourceId) {
    details = `Recurso: ${resourceId}\n\n${details}`;
  }

  return { header, details };
}
